const SERVICES = [
  {
    id: 'mock-survey',
    name: 'Mock Survey & Readiness Assessment',
    price: '$3,500-$7,500 per engagement',
    cadence: 'Annual or 6 months pre-survey',
    summary: 'A credentialed surveyor runs a simulation of your CMS, AAAHC, or TJC survey and turns findings directly into platform-tracked remediation.',
    includes: [
      '1-2 day remote or on-site mock survey',
      'Platform-scored readiness report with priority findings',
      'Auto-generated CAPA follow-up and evidence baseline',
    ],
    featureLink: 'Guided walkthrough, Report Viewer, Improvement Plans, and photo evidence baseline.',
  },
  {
    id: 'domain-audit',
    name: 'Focused Domain Audit',
    price: '$1,200-$2,500 per domain audit',
    cadence: 'Quarterly or as-needed',
    summary: 'A deep-dive on one high-risk area such as infection control, life safety, medication management, or environment of care.',
    includes: [
      'Half-day remote or on-site review',
      'Scoped walkthrough tied to one domain',
      'Targeted training and corrective-action recommendations',
    ],
    featureLink: 'Scoped walkthrough mode, domain trends, and recurring self-monitoring prompts.',
  },
  {
    id: 'qapi-design',
    name: 'QAPI Program Design & Implementation',
    price: '$4,000-$8,000 setup',
    cadence: 'One-time setup + annual review',
    summary: 'We stand up a facility-specific QAPI program with the indicators, committee structure, templates, and review cadence needed to satisfy CMS expectations.',
    includes: [
      '2-3 guided working sessions',
      'Indicator set and committee structure templates',
      '90-day check-in and annual review path',
    ],
    featureLink: 'Compliance trends, self-monitoring indicators, training assignments, and corrective-action workflows.',
  },
  {
    id: 'accreditation-prep',
    name: 'Accreditation Prep Package',
    price: '$15,000-$25,000 per engagement',
    cadence: 'Every 3 years or before survey',
    summary: 'A 3-6 month readiness engagement that combines mock survey work, document review, training prep, remediation tracking, and final readiness validation.',
    includes: [
      'Initial baseline visit plus monthly remote check-ins',
      'Document readiness, training, and remediation coordination',
      'Final readiness check 2 weeks before survey',
    ],
    featureLink: 'Prestudy document checklist, full guided walkthrough, CAPA workflow, and release-ready evidence exports.',
  },
  {
    id: 'compliance-retainer',
    name: 'Continuous Compliance Retainer',
    price: '$1,500-$3,000 per month',
    cadence: 'Monthly ongoing',
    summary: 'Ongoing expert support for facilities that want continuous oversight, quarterly mini-audits, and rapid access when regulators or complaints create pressure.',
    includes: [
      'Monthly 60-minute review call',
      'Unlimited email/chat support for compliance questions',
      'Quarterly mini-audit and regulatory-change review',
    ],
    featureLink: 'Notification-driven monthly review, state overlay updates, and score monitoring across the full compliance cycle.',
  },
] as const

const BUNDLES = [
  {
    name: 'Essentials Bundle',
    price: '$6,500 / year',
    audience: 'Independent ASCs between accreditation cycles',
    items: ['1 mock survey', '2 domain audits', 'Standalone platform access'],
  },
  {
    name: 'Accreditation Ready Bundle',
    price: '$22,000 one-time',
    audience: 'Facilities 6-12 months before survey',
    items: ['Accreditation prep package', 'QAPI design', '12 months platform access'],
  },
  {
    name: 'Total Compliance Bundle',
    price: '$4,500 / month',
    audience: 'Operators who want continuous expert coverage',
    items: ['Compliance retainer', '1 annual mock survey', '4 domain audits + platform access'],
  },
] as const

const FAQS = [
  {
    question: 'Do these services replace the platform subscription?',
    answer: 'No. The consulting services are designed to layer expert execution on top of the ASC Survey platform so your findings, remediation, and evidence stay in one governed workspace.',
  },
  {
    question: 'Can you support AAAHC, TJC, and CMS survey readiness?',
    answer: 'Yes. The service catalog is built on the same CMS-first platform foundation and crosswalk model used throughout the product, so the consulting motion stays aligned to your accreditor path.',
  },
  {
    question: 'How do consulting engagements hand off into day-to-day operations?',
    answer: 'Every engagement is intended to leave behind a live operating cadence: CAPAs in the platform, QAPI indicators, self-monitoring prompts, and evidence your team can keep running after the engagement ends.',
  },
] as const

const SERVICE_TYPE_PATHS: Record<string, string> = {
  'mock-survey': '/?service_type=mock_survey#demo',
  'domain-audit': '/?service_type=domain_audit#demo',
  'qapi-design': '/?service_type=qapi_design#demo',
  'accreditation-prep': '/?service_type=accreditation_prep#demo',
  'compliance-retainer': '/?service_type=compliance_retainer#demo',
  'Essentials Bundle': '/?service_type=essentials_bundle#demo',
  'Accreditation Ready Bundle': '/?service_type=accreditation_ready_bundle#demo',
  'Total Compliance Bundle': '/?service_type=total_compliance_bundle#demo',
}

function CtaLinks({ serviceId }: { serviceId: string }) {
  return (
    <div className="mt-6 flex flex-wrap gap-3">
      <a
        href={SERVICE_TYPE_PATHS[serviceId] ?? '/?service_type=general#demo'}
        className="rounded-full bg-[var(--brand-primary)] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[var(--brand-primary-dark)]"
      >
        Request a Quote
      </a>
      <a
        href={`/#solutions`}
        className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
      >
        See Platform Fit
      </a>
      <a
        href={`/services#${serviceId}`}
        className="rounded-full border border-transparent px-2 py-2 text-sm font-semibold text-[var(--brand-primary)] transition hover:text-[var(--brand-primary-dark)]"
      >
        Share This Section
      </a>
    </div>
  )
}

export function ConsultingServicesPage() {
  return (
    <main className="min-h-screen bg-slate-50 text-slate-900">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--brand-primary)]">
            Consulting Services
          </p>
          <div className="mt-4 grid gap-10 lg:grid-cols-[1.2fr_0.8fr]">
            <div>
              <h1 className="max-w-3xl text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl">
                Expert ASC compliance support that runs inside the same platform your team uses every day.
              </h1>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-600">
                Choose a focused audit, a full accreditation-prep engagement, or a continuous retainer.
                Every service is mapped to the ASC Survey workflows that keep your facility ready between visits.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="/#demo"
                  className="rounded-full bg-[var(--brand-primary)] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[var(--brand-primary-dark)]"
                >
                  Request a Consulting Quote
                </a>
                <a
                  href="/#solutions"
                  className="rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-100"
                >
                  Compare Platform Tiers
                </a>
              </div>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-slate-900 p-6 text-slate-50 shadow-sm">
              <p className="text-sm font-semibold uppercase tracking-[0.16em] text-emerald-300">ROI Snapshot</p>
              <p className="mt-4 text-3xl font-semibold">68-78%</p>
              <p className="mt-1 text-sm text-slate-300">of ASCs fail their first unannounced CMS survey.</p>
              <div className="mt-6 space-y-3 text-sm text-slate-300">
                <p>Follow-up surveys cost <span className="font-semibold text-white">$12,000-$18,000</span>.</p>
                <p>Systematic failures can trigger <span className="font-semibold text-white">$1.2M-$3.8M</span> in penalties.</p>
                <p>Medicare represents a median <span className="font-semibold text-white">39%</span> of ASC revenue.</p>
              </div>
              <p className="mt-6 text-sm leading-6 text-slate-300">
                The consulting catalog is designed to cost less than a failed survey while leaving behind a usable operating system for daily compliance.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16 sm:px-8">
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--brand-primary)]">Five Services</p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">Pick the engagement that matches the pressure you are under.</h2>
          <p className="mt-4 text-base leading-7 text-slate-600">
            Every service maps to existing platform workflows so findings, remediation, training, and evidence remain in the same governed workspace instead of splitting across decks and email chains.
          </p>
        </div>
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {SERVICES.map((service) => (
            <article key={service.id} id={service.id} className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-xl font-semibold text-slate-950">{service.name}</h3>
                  <p className="mt-2 text-sm font-semibold uppercase tracking-[0.14em] text-[var(--brand-primary)]">{service.price}</p>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{service.cadence}</span>
              </div>
              <p className="mt-4 text-sm leading-7 text-slate-600">{service.summary}</p>
              <ul className="mt-5 space-y-3 text-sm text-slate-700">
                {service.includes.map((item) => (
                  <li key={item} className="flex gap-3">
                    <span className="mt-1 h-2 w-2 rounded-full bg-emerald-500" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 rounded-2xl bg-slate-50 p-4 text-sm leading-6 text-slate-600">
                <span className="font-semibold text-slate-900">Platform fit:</span> {service.featureLink}
              </div>
              <CtaLinks serviceId={service.id} />
            </article>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:px-8">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--brand-primary)]">Bundles</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">Three ways to package the work.</h2>
            <p className="mt-4 text-base leading-7 text-slate-600">
              The bundles are designed for different operating rhythms: between surveys, ahead of a survey, or as a monthly outsourced compliance layer.
            </p>
          </div>
          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {BUNDLES.map((bundle) => (
              <article key={bundle.name} className="rounded-3xl border border-slate-200 bg-slate-50 p-7">
                <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[var(--brand-primary)]">{bundle.price}</p>
                <h3 className="mt-3 text-xl font-semibold text-slate-950">{bundle.name}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600">{bundle.audience}</p>
                <ul className="mt-5 space-y-3 text-sm text-slate-700">
                  {bundle.items.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="mt-1 h-2 w-2 rounded-full bg-[var(--brand-primary)]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={SERVICE_TYPE_PATHS[bundle.name] ?? '/?service_type=general#demo'}
                  className="mt-6 inline-flex rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 transition hover:border-slate-300 hover:bg-slate-100"
                >
                  Request a Quote
                </a>
              </article>
            ))}
          </div>
          <div className="mt-10 overflow-x-auto rounded-3xl border border-slate-200 bg-white">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-5 py-4 text-left font-semibold text-slate-700">Bundle</th>
                  <th className="px-5 py-4 text-left font-semibold text-slate-700">Mock Survey</th>
                  <th className="px-5 py-4 text-left font-semibold text-slate-700">Domain Audits</th>
                  <th className="px-5 py-4 text-left font-semibold text-slate-700">QAPI Design</th>
                  <th className="px-5 py-4 text-left font-semibold text-slate-700">Retainer</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <tr>
                  <td className="px-5 py-4 font-semibold text-slate-900">Essentials</td>
                  <td className="px-5 py-4 text-slate-600">1 included</td>
                  <td className="px-5 py-4 text-slate-600">2 included</td>
                  <td className="px-5 py-4 text-slate-600">Optional add-on</td>
                  <td className="px-5 py-4 text-slate-600">No</td>
                </tr>
                <tr>
                  <td className="px-5 py-4 font-semibold text-slate-900">Accreditation Ready</td>
                  <td className="px-5 py-4 text-slate-600">Baseline + final readiness</td>
                  <td className="px-5 py-4 text-slate-600">As needed during engagement</td>
                  <td className="px-5 py-4 text-slate-600">Included</td>
                  <td className="px-5 py-4 text-slate-600">No</td>
                </tr>
                <tr>
                  <td className="px-5 py-4 font-semibold text-slate-900">Total Compliance</td>
                  <td className="px-5 py-4 text-slate-600">1 annual</td>
                  <td className="px-5 py-4 text-slate-600">4 quarterly</td>
                  <td className="px-5 py-4 text-slate-600">Included as needed</td>
                  <td className="px-5 py-4 text-slate-600">Monthly included</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16 sm:px-8">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--brand-primary)]">FAQ</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">Common questions from ASC operators.</h2>
          </div>
          <div className="space-y-4">
            {FAQS.map((faq) => (
              <article key={faq.question} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-950">{faq.question}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600">{faq.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  )
}
